"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

type DelayedGoogleTagManagerProps = {
  gtmId: string;
  nonce?: string;
  delayMs?: number;
};

declare global {
  interface Window {
    dataLayer?: Object[];
  }
}

const IMMEDIATE_LOAD_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;

export default function DelayedGoogleTagManager({
  gtmId,
  nonce,
  delayMs = 12000,
}: DelayedGoogleTagManagerProps) {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) {
      return;
    }

    let loaded = false;

    const load = () => {
      if (loaded) {
        return;
      }

      loaded = true;
      setShouldLoad(true);
    };

    const timeoutId = window.setTimeout(load, delayMs);

    IMMEDIATE_LOAD_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, load, { once: true, passive: true });
    });

    return () => {
      window.clearTimeout(timeoutId);

      IMMEDIATE_LOAD_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, load);
      });
    };
  }, [delayMs, shouldLoad]);

  if (!shouldLoad) {
    return null;
  }

  const encodedGtmId = encodeURIComponent(gtmId);

  return (
    <>
      <Script
        id="gtm-init"
        strategy="afterInteractive"
        nonce={nonce}
        data-cookieconsent="ignore"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || []; window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });`,
        }}
      />
      <Script
        id="gtm-script"
        src={`https://www.googletagmanager.com/gtm.js?id=${encodedGtmId}`}
        strategy="afterInteractive"
        nonce={nonce}
        data-cookieconsent="ignore"
      />
    </>
  );
}
